import { logDev, logEvent } from "@agtech/core/logging/AgTechLogger"
import { v4 as uuidv4 } from 'uuid';
import { IAgTechGridItem } from "./AgTechGridData";
import { AgTechGridProps } from "./AgTechGridProps";
import { AgTechGridEntityDataState, AgTechGridEntityIndex, AgTechGridStateHandlingProps, AgTechGridState } from "./AgTechGridState";
import { DoSafely } from "@agtech/core/utilities/AgTechUtilities";

export type AgTechGridEntity<TItem  extends IAgTechGridItem> = {
    id: string,
    item: TItem,
    state: AgTechGridEntityState,
    versionNumber: number,
    attributes?: {
        isLastItemInGroup?: boolean
    }
}

export type AgTechGridEntityState = {
    wasCreated: boolean,
    wasWritten: boolean,
    status: AgTechGridEntityStatus,
    isSelected: boolean,
    hasBeenWarned?: boolean,
    message?: string,
    icon?: string,
}

export enum AgTechGridEntityStatus {
    Default,
    Created,
    Updated,
    Invalid
};

export const initializeGridEntities = <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridProps<TItem, TContextData>,
): AgTechGridEntityDataState<TItem> => {
    const doesGridFocusFirstOnInit = props.focusConfig?.focusFirstItem ?? false;

    let focusedEntityId = '';
    const allEntitiesIndex: AgTechGridEntityIndex<TItem> = {};

    const gridData = props.data.itemQuery.queryResult?.data ?? [];

    DoSafely(() => {
        gridData.items.forEach((item, index) => {
            const itemEntity = getGridEntityForNewItem(item, props);
    
            if (doesGridFocusFirstOnInit && index === 0) {
                focusedEntityId = itemEntity.id;
            }
    
            allEntitiesIndex[itemEntity.id] = itemEntity;
        });
    
        const allEntities = Object.values(allEntitiesIndex);
    
        // If there are no entities to show in the grid and the grid supports editing,
        // add a default item to the entity list
        if (allEntities.length === 0 && props.editConfig && props.editConfig.defaultItem) {
            const defaultItem = props.editConfig.defaultItem([]);
            const defaultEntity = createGridEntityFromItem(defaultItem, props);
    
            if (defaultEntity) {
                allEntitiesIndex[defaultEntity.id] = { ...defaultEntity };
                
                if (doesGridFocusFirstOnInit) {
                    focusedEntityId = defaultEntity.id;
                }
            }
        }
    });

    return {
        allEntities: allEntitiesIndex,
        focusedEntityId: focusedEntityId,
        hasLoaded: true,
        versionNumber: 0
    };
}

export const updateEntitiesOnEntityFocused = async <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridStateHandlingProps<TItem, TContextData>,
    focusedEntity: AgTechGridEntity<TItem>
) => {
    let gridState = props.gridState();

    if (gridState.gridProps.focusConfig) {
        await gridState.gridProps.focusConfig.onItemFocused(focusedEntity.item);

        props.updateGridEntities({
            ...gridState.entityState,
            focusedEntityId: focusedEntity.id
        });
    }
}

export const updateEntitiesOnEntitySelected = async <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridStateHandlingProps<TItem, TContextData>,
    selectedEntity: AgTechGridEntity<TItem>
) => {
    let gridState = props.gridState();

    logDev('Updating grid entities for entity selected');

    if (gridState.gridProps.selectionConfig) {
        let updatedItem = await gridState.gridProps.selectionConfig.onItemSelected(selectedEntity.item);

        if (!updatedItem) {
            updatedItem = selectedEntity.item;
        }

        gridState.entityState.allEntities[updatedItem.id] = {
            ...gridState.entityState.allEntities[updatedItem.id],
            state: {
                ...gridState.entityState.allEntities[updatedItem.id].state,
                isSelected: true
            }
        };

        props.updateGridEntities({
            ...gridState.entityState
        });
    }
}

export const updateEntitiesOnEntityDeselected = async <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridStateHandlingProps<TItem, TContextData>,
    selectedEntity: AgTechGridEntity<TItem>
) => {
    let gridState = props.gridState();

    logDev('Updating grid entities for entity deselected');

    if (gridState.gridProps.selectionConfig) {
        let updatedItem = await gridState.gridProps.selectionConfig.onItemDeSelected(selectedEntity.item);

        if (!updatedItem) {
            updatedItem = selectedEntity.item;
        }

        gridState.entityState.allEntities[updatedItem.id] = {
            ...gridState.entityState.allEntities[updatedItem.id],
            state: {
                ...gridState.entityState.allEntities[updatedItem.id].state,
                isSelected: false
            }
        };

        props.updateGridEntities({
            ...gridState.entityState
        });
    }
}

export const updateEntitiesForNewDefaultEntity = <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridStateHandlingProps<TItem, TContextData>
) => {
    let gridState = props.gridState();
    let gridEditConfig = gridState.gridProps.editConfig;

    if (gridEditConfig && gridEditConfig.defaultItem && gridEditConfig.onEntityCreated) {
        let currentItems = Object.values(gridState.entityState.allEntities).map(entity => entity.item);
        let newDefaultItem = gridEditConfig.defaultItem(currentItems);
        let newDefaultEntityId = newDefaultItem.id ? newDefaultItem.id.toString() : uuidv4();

        logEvent('Grid item created', {
            id: newDefaultEntityId,
            item: newDefaultItem
        });
    
        gridState.entityState.allEntities[newDefaultEntityId] = {
            id: newDefaultEntityId,
            item: {...newDefaultItem},
            state: {
                wasCreated: true,
                wasWritten: false,
                isSelected: false,
                status: AgTechGridEntityStatus.Created
            },
            versionNumber: 0
        };

        props.updateGridEntities({
            ...gridState.entityState,
            focusedEntityId: newDefaultEntityId
        });

        setTimeout(() => {
            let gridRef = gridState.gridSurfaceRef;

            if (gridRef && gridRef.current) {
                let gridRows = gridRef.current.querySelectorAll(`.agtech-grid-surface-body-row`);
                let gridRowForNewEntity = gridRows[gridRows.length - 1];
        
                if (gridRowForNewEntity) {    
                    let newRowInputs: any = gridRowForNewEntity.querySelector('input:not([type="checkbox"])');
        
                    if (newRowInputs) {
                        newRowInputs.focus();
                    }
                }
            }
        }, 300);
    }
}

export const updateEntitiesOnEntityCreated = <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridStateHandlingProps<TItem, TContextData>,
    createdEntity: AgTechGridEntity<TItem>
) => {
    let gridState = props.gridState();

    gridState.entityState.allEntities[createdEntity.id] = { ...createdEntity };

    logDev('Updating grid entities for entity created');
    
    props.updateGridEntities({
        ...gridState.entityState
    });
}

export const updateEntitiesOnEntityUpdated = <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridStateHandlingProps<TItem, TContextData>,
    updatedEntity: AgTechGridEntity<TItem>
) => {
    let gridState = props.gridState();
    gridState.entityState.allEntities[updatedEntity.id] = { ...updatedEntity };

    logDev('Updating grid entities for entity updated');

    props.updateGridEntities({
        ...gridState.entityState
    });
}

export const updateEntitiesOnEntityRemoved = <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridStateHandlingProps<TItem, TContextData>,
    removedEntity: AgTechGridEntity<TItem>
) => {
    let gridState = props.gridState();
    delete gridState.entityState.allEntities[removedEntity.id];

    logDev('Updating grid entities for entity removed');

    props.updateGridEntities({
        ...gridState.entityState
    });
}

export const updateEntitiesOnItemsChanged = <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridProps<TItem, TContextData>,
    entityState: AgTechGridEntityDataState<TItem>,
    updateGridEntities: (updatedEntityData: AgTechGridEntityDataState<TItem>) => void,
) => {
    if (entityState.hasLoaded) {
        DoSafely(() => {
            const updatedEntityIndex = getUpdatedEntitiesOnItemsChanged(props, entityState);

            updateGridEntities({
                ...entityState,
                allEntities: updatedEntityIndex
            });
        });
    }
}

export const getUpdatedEntitiesOnItemsChanged = <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridProps<TItem, TContextData>,
    entityState: AgTechGridEntityDataState<TItem>
) => {
    const updatedEntityIndex: AgTechGridEntityIndex<TItem> = { };

    const gridData = props.data.itemQuery.queryResult.data ?? [];

    if (entityState.hasLoaded) {
        gridData.items.forEach(item => {
            let existingEntity = entityState.allEntities[item.id];

            if (existingEntity) {
                updatedEntityIndex[existingEntity.id] = {
                    ...existingEntity,
                    item: { ...existingEntity.item, ...item }
                };
            }
            else {
                updatedEntityIndex[item.id] = getGridEntityForNewItem(item, props);
            }
        });
    }

    return updatedEntityIndex;
}

export const getEntitiesAvailableBySearchState = <TItem extends IAgTechGridItem, TContextData = {}>(
    gridState: AgTechGridState<TItem, TContextData>
): AgTechGridEntityIndex<TItem> => {
    let entities = Object.values(gridState.entityState.allEntities);

    logDev('Updating grid entities for update in search state');

    if (gridState.searchState) {
        let searchTerm = gridState.searchState.filter;
        let searchField = gridState.searchState.selectedField;

        if (searchField) {
            entities = entities.filter(entity => {
                return (entity.state.wasCreated && !entity.state.wasWritten) ||
                    searchField.value(entity.item).toLowerCase().includes(searchTerm.toLowerCase())
            });
        }
    }
    
    return indexGridEntities(entities);
}

export const getEntitiesAvailableByView = <TItem extends IAgTechGridItem, TContextData = {}>(
    gridState: AgTechGridState<TItem, TContextData>,
    availableEntityIndex: AgTechGridEntityIndex<TItem>
): AgTechGridEntityIndex<TItem> => {
    let availableEntities = Object.values(availableEntityIndex);

    logDev('Updating grid entities for update in selected view');

    if (gridState.selectedView) {
        availableEntities = availableEntities.filter(entity => gridState.selectedView?.filter(entity.item));
    }
    
    return indexGridEntities(availableEntities);
}

const indexGridEntities = <TItem extends IAgTechGridItem>(entities: AgTechGridEntity<TItem>[]): AgTechGridEntityIndex<TItem> => {
    let entityIndex: AgTechGridEntityIndex<TItem> = {};

    entities.forEach(entity => {
        entityIndex[entity.id] = entity;
    });

    return entityIndex;
}

const getGridEntityForNewItem = <TItem extends IAgTechGridItem, TContextData = {}>(
    item: TItem,
    props: AgTechGridProps<TItem, TContextData>
): AgTechGridEntity<TItem> => {
    if (item.id === undefined) {
        logEvent('GridEntityWithNoId', item);
    }

    return {
        id: item.id?.toString() ?? '',
        item: {...item},
        state: {
            wasCreated: false,
            wasWritten: false,
            status: AgTechGridEntityStatus.Default,
            isSelected: props.selectionConfig ? props.selectionConfig.isItemSelected(item) : false
        },
        versionNumber: 0
    };
}

const createGridEntityFromItem = <TItem extends IAgTechGridItem, TContextData = {}>(
    item: TItem,
    props: AgTechGridProps<TItem, TContextData>
): AgTechGridEntity<TItem> | null => {
    let gridEntity: AgTechGridEntity<TItem> | null = null;

    if (props.editConfig) {
        gridEntity = {
            id: item.id.toString(),
            item: { ...item },
            state: {
                wasCreated: true,
                wasWritten: false,
                status: AgTechGridEntityStatus.Created,
                isSelected: false
            },
            versionNumber: 0
        };
    }

    return gridEntity;
}